import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useAuthUser } from 'auth/hooks';
import { ContactGroupType } from 'contacts/enums';
import { useContactActions } from 'contacts/hooks';
import { IContact, IContactForm, IContactGroup } from 'contacts/types';
import { AccessPermissions } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { hasPermission } from 'router/utils';

import { mapContactForm } from './mappers';
import { contactSchema } from './schema';
import { UpsertContactFormFields } from './UpsertContactFormFields';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  contact?: IContact;
  contactGroup?: Partial<IContactGroup>;
  onSubmitSuccess?: (contact: IContact, contactGroup?: IContactGroup) => void;
  initialValues?: Partial<IContactForm>;
  level?: number;
  /** Email domain the user confirmed attaching to the contact group; see `mapContactForm`. */
  attachDomainConfirmed?: string;
}

export const UpsertContact = ({
  isOpen,
  onClose,
  contact,
  contactGroup: initialContactGroup,
  onSubmitSuccess,
  initialValues,
  level,
  attachDomainConfirmed,
}: IProps) => {
  const { clearNotification } = useNotificationContext();

  const { saveContact } = useContactActions();

  const { user } = useAuthUser();

  const contactGroup = useMemo(() => {
    if (initialContactGroup) {
      return initialContactGroup;
    }

    return contact?.contactGroup;
  }, [initialContactGroup, contact]);

  const hasDefaultVendorEditPermission = hasPermission(
    [AccessPermissions.ACCESS_COMPANY_DEFAULT_VENDOR_EDIT],
    user?.resources
  );

  const isVendorContactGroup = useMemo(() => {
    return contactGroup?.isVendor || contactGroup?.type?.includes(ContactGroupType.VENDOR);
  }, [contactGroup]);

  const hiddenFields = useMemo(() => {
    return {
      isDefault: !isVendorContactGroup || !hasDefaultVendorEditPermission,
    };
  }, [isVendorContactGroup, hasDefaultVendorEditPermission]);

  const defaultValues = useMemo(() => {
    if (!contact?.id) {
      return {
        ...(!hiddenFields?.isDefault && {
          isDefault: false,
        }),
        ...initialValues,
      };
    }

    return {
      id: contact.id,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      mobile: contact.mobile,
      contactGrouping: contact.contactGroupingName,
      billTo: contact.billingAddress,
      shipTo: contact.shippingAddress,
      soldTo: contact.sellingAddress,
      sendInvitation: false,
      ...(!hiddenFields?.isDefault && {
        isDefault: contact.isDefault || false,
      }),
    };
  }, [contact, initialValues, hiddenFields]);

  const onSubmit = (reset: UseFormReset<IContactForm>) => async (data: IContactForm) => {
    const { success, contact: updatedContact } = await saveContact(
      mapContactForm(data, contactGroup?.id, Boolean(contact?.deleted), attachDomainConfirmed)
    );

    if (!success) {
      return false;
    }

    onSubmitSuccess?.(updatedContact, updatedContact?.group);

    setTimeout(() => handleOnClose(reset), 1000);
  };

  const handleOnClose = (reset: UseFormReset<IContactForm>) => {
    clearNotification();
    onClose();

    reset(defaultValues as IContactForm);
  };

  return (
    <FormProvider schema={contactSchema()} defaultValues={defaultValues} resetDefaultValues>
      <UpsertContactFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        hiddenFields={hiddenFields}
        contact={contact}
        contactGroup={contactGroup}
        onSubmit={onSubmit}
        level={level}
      />
    </FormProvider>
  );
};
